function TermsOfUseHealcore() {
  return (
    <div>
      <h2>HealCore - Terms of Service for HealCore AI</h2>
      <p>
        <strong>Last Updated:</strong> <span>July 1, 2025</span>
      </p>

      <p>
        This Terms of Service agreement (this <strong>“Agreement”</strong>) is a legally binding contract between the individual
        or entity agreeing to these terms (<strong>“User”</strong>, <strong>“you”</strong>, or <strong>“your”</strong>) and
        HealCore Tissue Partners, a Texas Limited Liability Company (<strong>“HealCore”</strong>, <strong>“we”</strong>,
        <strong>“us”</strong>, or <strong>“our”</strong>). This Agreement governs your access to and use of the HealCore AI
        platform, including any related software, services, and websites (collectively, the <strong>“Platform”</strong>).
      </p>

      <p>
        The Platform is powered by a proprietary ambient artificial intelligence transcription software engine and related
        technology (the <strong>"Software"</strong>) licensed from Lucas Health Corporation, who is the
        <strong>"Technology Provider"</strong>.
      </p>

      <p>
        By creating an account, clicking <em>“I accept”</em>, or by accessing or using the Platform in any manner, you affirm that
        you have read, fully understand, and agree to be legally bound by this entire Agreement. If you are entering into this
        Agreement on behalf of a company or other legal entity, you represent and warrant that you have the full legal authority
        to bind such entity to this Agreement.
      </p>

      <p>
        <strong>
          THIS AGREEMENT CONTAINS A MANDATORY ARBITRATION PROVISION, A WAIVER OF CLASS ACTION RIGHTS, AND COMPREHENSIVE
          INDEMNIFICATION OBLIGATIONS. IT PLACES SOLE AND ULTIMATE RESPONSIBILITY ON YOU FOR THE ACCURACY OF ALL DOCUMENTATION AND
          FOR ANY FINANCIAL OR REGULATORY CONSEQUENCES, INCLUDING AUDITS AND CLAWBACKS. THE PLATFORM IS PROVIDED WITHOUT ANY
          GUARANTEE OF ACCURACY. PLEASE READ THIS DOCUMENT CAREFULLY IN ITS ENTIRETY.
        </strong>
      </p>

      <h3>The Platform: Purpose and Core Limitations</h3>
      <p>
        The Platform is an ambient artificial intelligence transcription and documentation support tool designed exclusively to
        improve workflow efficiencies for qualified healthcare providers (<strong>“Providers”</strong>) by capturing and
        documenting authentic patient-provider interactions. You acknowledge that the Platform is a tool to assist with
        documentation, not to create final, verified records. All ultimate responsibility for clinical decisions, documentation
        accuracy, and regulatory compliance remains exclusively with you.
      </p>

      <h3>Account and User Security</h3>
      <p>
        You must provide accurate and current information upon registration. You are solely and entirely responsible for all
        activity that occurs under your account. You must implement and maintain reasonable administrative, physical, and
        technical safeguards to protect your account credentials. You must notify HealCore immediately in writing of any
        unauthorized use or security breach related to your account. Neither HealCore nor its Technology Provider is liable for
        any loss or damage arising from your failure to comply with these security obligations.
      </p>

      <h3>Intellectual Property and Platform Ownership</h3>
      <p>
        <strong>3.1. Ownership.</strong> You acknowledge and agree that the Platform, including the underlying Software, source
        code, architecture, algorithms, and all associated intellectual property rights, is the sole and exclusive property of our
        Technology Provider, Lucas Health Corporation. The Platform is licensed to you on a limited basis; it is not sold. This
        Agreement grants you a limited, revocable, non-exclusive, non-sublicensable, and non-transferable license to access and
        use the Platform solely for its intended purpose.
      </p>
      <p>
        <strong>3.2. Restrictions.</strong> You are expressly prohibited from: (a) creating derivative works from the Platform or
        Software; (b) accessing the source code or other non-public source materials; (c) sublicensing, selling, renting, or
        otherwise commercializing the Platform; or (d) copying, decompiling, modifying, or reverse engineering the Platform or
        Software.
      </p>
      <p>
        <strong>3.3. Trademarks.</strong> The marks HEALCORE, HEALCORE AI, and our associated logos are our exclusive trademarks.
        The marks LUCAS HEALTH and LUCAS AI are the exclusive trademarks of our Technology Provider.
      </p>

      <h3>Prohibited Conduct and Intended Use</h3>
      <p>
        Your use of the Platform must be legal, ethical, and strictly for its intended use. The Platform is explicitly not
        intended for, and you are strictly forbidden from using it to:
      </p>
      <ul>
        <li>Create false, misleading, fraudulent, or artificially generated medical documentation;</li>
        <li>
          Manufacture compliance documentation for services not authentically rendered or for patient encounters that did not
          occur;
        </li>
        <li>Generate documentation to reverse-engineer or artificially create medical compliance records;</li>
        <li>
          Facilitate, justify, or encourage the overutilization or misuse of any medical treatments, biologics, tissues, or
          pharmaceuticals;
        </li>
        <li>
          Engage in any activity that constitutes, facilitates, or could be construed as Medicare fraud, Medicaid fraud, insurance
          fraud, healthcare billing abuse, or a violation of any applicable local, state, or federal law, including but not
          limited to the Anti-Kickback Statute, the Physician Self-Referral Law (Stark Law), or the False Claims Act.
        </li>
      </ul>

      <h3>Indemnification (Your Comprehensive Obligation to Protect Us and Our Technology Provider)</h3>
      <p>
        You agree to defend, indemnify, and hold harmless HealCore, its Technology Provider (Lucas Health Corporation), and their
        respective affiliates, officers, directors, employees, agents, and licensors (collectively, the
        <strong>“Indemnified Parties”</strong>) from and against any and all third-party claims, demands, suits, actions,
        proceedings, government investigations, audits, judgments, losses, damages, liabilities, fines, penalties, and expenses of
        any kind (including reasonable attorneys' fees and all legal costs), and any demands for recoupment or clawback from any
        payer (collectively, <strong>“Claims”</strong>) arising out of or in any way related to:
      </p>
      <ul>
        <li>Your material breach of this Agreement, negligence, willful misconduct, or violation of any applicable law;</li>
        <li>Your User Data, including Claims that it infringes third-party rights;</li>
        <li>Your use, misuse, or inability to use the Platform;</li>
        <li>Your clinical malpractice, professional negligence, or violation of any professional standard of care;</li>
        <li>
          Your documentation, billing, or coding practices, including but not limited to Claims arising from alleged fraudulent
          documentation, intentional billing fraud, or any other violation of healthcare regulations resulting from your
          operational practices;
        </li>
        <li>
          Any custom guidelines, templates, or protocols you provide for use with the Platform that violate applicable laws or
          regulations; and
        </li>
        <li>
          Any Claim whatsoever resulting from the inaccuracy, incompleteness, or alleged insufficiency of any documentation
          generated by the Platform that you failed to independently verify and correct.
        </li>
      </ul>

      <h3>Warranty Exclusions and Limitation of Liability</h3>
      <p>
        <strong>6.1. WARRANTY DISCLAIMER.</strong> THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE," WITH ALL FAULTS AND
        WITHOUT WARRANTY OF ANY KIND. TO THE MAXIMUM EXTENT PERMITTED BY LAW, HEALCORE AND ITS TECHNOLOGY PROVIDER (LUCAS HEALTH
        CORPORATION) EXPRESSLY DISCLAIM ANY AND ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING ALL IMPLIED
        WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. NEITHER WE NOR OUR
        TECHNOLOGY PROVIDER WARRANT THAT THE PLATFORM WILL BE ERROR-FREE OR THAT INFORMATION OBTAINED FROM IT WILL BE ACCURATE OR
        RELIABLE. THERE IS NO GUARANTEE AS TO THE ACCURACY, COMPLETENESS, OR SUITABILITY OF ANY OUTPUT FOR ANY PURPOSE, INCLUDING
        CLINICAL DOCUMENTATION OR BILLING.
      </p>
      <p>
        <strong>6.2. EXCLUSION OF DAMAGES.</strong> UNDER NO CIRCUMSTANCES WILL HEALCORE OR ITS TECHNOLOGY PROVIDER BE LIABLE TO
        YOU FOR ANY INCIDENTAL, CONSEQUENTIAL, INDIRECT, SPECIAL, PUNITIVE, OR EXEMPLARY DAMAGES.
      </p>
      <p>
        <strong>6.3. MAXIMUM AGGREGATE LIABILITY.</strong> IN NO EVENT SHALL THE TOTAL AGGREGATE LIABILITY OF HEALCORE AND ITS
        TECHNOLOGY PROVIDER (LUCAS HEALTH CORPORATION) EXCEED THE GREATER OF (A) THE TOTAL FEES PAID BY YOU TO HEALCORE DURING THE
        TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED U.S. DOLLARS ($100.00).
      </p>

      <h3>User’s Sole Responsibility for Verification, Accuracy, and All Outcomes</h3>
      <p>
        <strong>7.1. Acknowledgment of AI Limitations.</strong> You understand and acknowledge that the Platform uses artificial
        intelligence, which is an experimental technology. The output generated by the Platform is an automated draft and may
        contain factual inaccuracies, errors, or omissions. It is not a finished medical or legal document.
      </p>
      <p>
        <strong>7.2. Absolute Duty to Verify.</strong> You have an absolute, primary, and non-delegable duty to independently
        review, edit, and verify every part of the output generated by the Platform before signing, approving, or using it for any
        purpose, including but not limited to, entry into a medical record, clinical decision-making, or submission for billing or
        reimbursement. You must correct any and all inaccuracies.
      </p>
      <p>
        <strong>7.3. Sole Liability for All Consequences.</strong> You are solely and exclusively liable for all content within
        your final clinical documentation and for any and all outcomes resulting from that documentation. HealCore and Lucas
        Health Corporation shall bear no liability or responsibility whatsoever for any adverse consequences arising from your use
        of the Platform's output, including but not limited to: (a) inappropriate clinical decision-making; (b) any
        misrepresentation or fraudulent activity; (c) denied claims, payer audits, recoupments, or clawbacks; (d) government or
        regulatory investigations or penalties; or (e) any action that could trigger investigation by any regulatory authority.
      </p>
      <p>
        <strong>7.4. Advisory Nature.</strong> All tools and features within the Platform are strictly advisory. They are designed
        only to identify potential documentation gaps based on captured dialogue and are not a substitute for your professional
        medical, legal, and billing judgment.
      </p>

      <h3>Lucas Health Corporation as Third-Party Beneficiary</h3>
      <p>
        You acknowledge and agree that Lucas Health Corporation, our Technology Provider, is an intended third-party beneficiary
        of this Agreement. Lucas Health Corporation has the express right to enforce any and all provisions of this
        Agreement—including but not limited to Sections 3, 4, 5, 6, and 7—directly against you. Notwithstanding this right, your
        sole recourse for any and all claims arising from this Agreement shall be exclusively against HealCore, and not against
        Lucas Health Corporation.
      </p>

      <h3>Termination</h3>
      <p>
        We may suspend or terminate your account and this Agreement immediately for any material breach. Upon termination, your
        right to use the Platform ceases. The following sections shall survive any termination: 3, 4, 5, 6, 7, 8, 10, and 11.
      </p>

      <h3>Governing Law and Dispute Resolution</h3>
      <p>
        <strong>10.1. Governing Law.</strong> This Agreement is governed by the laws of the State of Texas.
      </p>
      <p>
        <strong>10.2. Mandatory Arbitration.</strong> All disputes shall be resolved exclusively by final and binding arbitration
        in Harris County, Texas.
      </p>
      <p>
        <strong>10.3. Class Action Waiver.</strong> YOU AND HEALCORE AGREE TO BRING CLAIMS AGAINST THE OTHER ONLY IN AN INDIVIDUAL
        CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
      </p>

      <h3>Miscellaneous Provisions</h3>
      <p>
        <strong>11.1. Entire Agreement.</strong> This Agreement, along with our Privacy Policy and Business Associate Agreement
        (if applicable), is the entire agreement between you and HealCore.
      </p>
      <p>
        <strong>11.2. Severability.</strong> If any provision is found to be unenforceable, it will be limited or eliminated to
        the minimum extent necessary so that this Agreement will otherwise remain in full force.
      </p>
      <p>
        <strong>11.3. Contact Information.</strong> For legal notices, contact us at: info@hctp.ai or 9002 Six Pines Drive, Ste.
        G, Shenandoah, TX 77380
      </p>
    </div>
  );
}

export default TermsOfUseHealcore;
